import { Circle, Ellipse, Group, Path, Rect } from 'react-konva';
import {
  SVGCircle,
  SVGEllipse,
  SVGPath,
  SVGRectangle,
  SVGShape,
  SVGShapeType,
  SVGShapes,
} from '../../../constants/icons/icons';
import { UseGridOperations } from '../../../hooks/useGridOperations';
import { SheetCommandType } from '../../../types/commands/sheetCommands/sheetCommands';
import { IconSetType } from '../../../types';
import { UseKonvaCursor } from '../../../../core/hooks/useKonvaCursor';

interface CellIconProps
  extends Pick<UseGridOperations, 'performSheetOperation'>,
    UseKonvaCursor {
  x: number;
  ci: number;
  ri: number;
  y: number;
  size: number;
  sheetId: string;
  shapes: SVGShapes;
  viewBoxSize: number;
  primaryColor: string;
  secondaryColor: string;
  cellIncludesValue: boolean;
  value: string | number;
  setType: IconSetType;
  cellValue: string;
  numericCellValue: number;
}

const CellIcon = ({
  x,
  y,
  size,
  viewBoxSize,
  shapes,
  ci,
  ri,
  primaryColor,
  secondaryColor,
  value,
  cellIncludesValue,
  setType,
  numericCellValue,
  sheetId,
  cellValue,
  onMouseEnter,
  onMouseLeave,
  performSheetOperation,
}: CellIconProps) => {
  const isSelected = isIconSelected(
    cellValue,
    numericCellValue,
    value,
    setType
  );
  // #region functions
  const onIconSelect = () => {
    let newValue: string | number = '';
    const numValue = Number(value);
    if (setType === IconSetType.Multiple) {
      newValue = !cellValue.length
        ? numValue
        : isSelected
        ? numericCellValue & ~numValue
        : numericCellValue | numValue;

      if (newValue === 0) newValue = '';
    } else if (setType === IconSetType.Progressive) {
      newValue = isSelected && numValue === numericCellValue ? '' : value;
    } else if (!isSelected) {
      newValue = value;
    }

    performSheetOperation({
      type: SheetCommandType.CellInput,
      sheetId,
      position: { ci, ri },
      value: newValue.toString(),
    });
  };

  const getVisibleFill = (fill?: string, fillOpposite?: boolean) =>
    fillOpposite ? oppositeColor : fill || pathColor;

  const scaleIcon = (num = 0, base = 0) => base + num * innerIconScale;
  // #endregion

  // #region variables
  const pathColor = isSelected ? secondaryColor : primaryColor;
  const oppositeColor = isSelected ? primaryColor : secondaryColor;
  const pathOpacity = cellIncludesValue ? (isSelected ? 1 : 0.4) : 0.7;

  const innerSinglePadding = 2;
  const innerIconSize = size - innerSinglePadding * 4;
  const innerIconScale = innerIconSize / viewBoxSize;
  const iconPosition = innerSinglePadding * 2;
  // #endregion

  // #region views
  const renderContainer = () => (
    <Rect
      width={size}
      height={size}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      cornerRadius={5}
    />
  );

  const renderSelectedBox = () =>
    isSelected && (
      <Rect
        x={0}
        y={0}
        width={size}
        height={size}
        fill={primaryColor}
        cornerRadius={5}
        listening={false}
      />
    );

  const drawRect = (
    {
      x,
      y,
      width,
      height,
      fill,
      fillOpposite,
      cornerRadius,
      hasStroke,
      strokeWidth,
    }: SVGRectangle,
    index: number
  ) => {
    const visibleFill = getVisibleFill(fill as string, fillOpposite);

    const scaledX = scaleIcon(x, iconPosition);
    const scaledY = scaleIcon(y, iconPosition);
    const scaledWidth = scaleIcon(width);
    const scaledHeight = scaleIcon(height);
    const scaledRadius =
      cornerRadius && typeof cornerRadius === 'number'
        ? scaleIcon(cornerRadius)
        : undefined;

    return (
      <Rect
        key={index}
        x={scaledX}
        y={scaledY}
        width={scaledWidth}
        height={scaledHeight}
        fill={visibleFill}
        cornerRadius={scaledRadius}
        stroke={hasStroke ? visibleFill : ''}
        strokeWidth={strokeWidth}
      />
    );
  };

  const drawCircle = (
    { x, y, radius, fill, fillOpposite, hasStroke, strokeWidth }: SVGCircle,
    index: number
  ) => {
    const scaledX = scaleIcon(x, iconPosition); // iconPosition + x * innerIconScale;
    const scaledY = scaleIcon(y, iconPosition); //iconPosition + y * innerIconScale;
    const scaledRadius = scaleIcon(radius); //radius * innerIconScale;
    const visibleFill = getVisibleFill(fill as string, fillOpposite);
    return (
      <Circle
        key={index}
        x={scaledX}
        y={scaledY}
        radius={scaledRadius}
        fill={visibleFill}
        stroke={hasStroke ? visibleFill : ''}
        strokeWidth={strokeWidth}
      />
    );
  };

  const drawEllipse = (
    {
      x,
      y,
      radiusX,
      radiusY,
      fill,
      fillOpposite,
      hasStroke,
      strokeWidth,
    }: SVGEllipse,
    index: number
  ) => {
    const scaledX = scaleIcon(x, iconPosition); // iconPosition + x * innerIconScale;
    const scaledY = scaleIcon(y, iconPosition); // iconPosition + y * innerIconScale;
    const scaledRadiusX = scaleIcon(radiusX); // radiusX * innerIconScale;
    const scaledRadiusY = scaleIcon(radiusY); // radiusY * innerIconScale;
    const visibleFill = getVisibleFill(fill as string, fillOpposite);

    return (
      <Ellipse
        key={index}
        x={scaledX}
        y={scaledY}
        radiusX={scaledRadiusX}
        radiusY={scaledRadiusY}
        fill={visibleFill}
        stroke={hasStroke ? visibleFill : ''}
        strokeWidth={strokeWidth}
      />
    );
  };

  const drawPath = (path: SVGPath, index: number) => {
    const { d, fill, fillOpposite, strokeWidth, hasStroke } = path;
    const visibleFill = getVisibleFill(fill as string, fillOpposite);
    return (
      <Path
        key={index}
        data={d}
        scaleX={innerIconScale}
        scaleY={innerIconScale}
        x={iconPosition}
        y={iconPosition}
        fill={visibleFill}
        strokeWidth={strokeWidth}
        stroke={hasStroke ? visibleFill : ''}
        lineCap={path.lineCap}
        fillRule={path.fillRule}
        lineJoin={path.lineJoin}
        opacity={pathOpacity}
        listening={false}
      />
    );
  };

  const drawShape = (shape: SVGShape, index: number) => {
    switch (shape.type) {
      case SVGShapeType.Path:
        return drawPath(shape, index);
      case SVGShapeType.Circle:
        return drawCircle(shape, index);
      case SVGShapeType.Ellipse:
        return drawEllipse(shape, index);
      case SVGShapeType.Rectangle:
        return drawRect(shape, index);
    }
  };

  const renderSVGShapes = () =>
    shapes.map((shape, index) => drawShape(shape, index));
  // #endregion

  return (
    <Group x={x} y={y} onMouseDown={onIconSelect}>
      {renderContainer()}
      {renderSelectedBox()}
      {renderSVGShapes()}
    </Group>
  );
};

// #region helpers
const isIconSelected = (
  cellValue: string,
  numericCellValue: number,
  value: string | number,
  setType: IconSetType
) => {
  if (!cellValue.length) return false;
  switch (setType) {
    case IconSetType.Single:
      return value.toString() === cellValue;
    case IconSetType.Multiple:
    case IconSetType.Progressive:
      if (isNaN(numericCellValue)) return false;
      const numericValue = Number(value);
      if (isNaN(numericValue)) return false;
      const isMultipleCheck = setType === IconSetType.Multiple;

      return isMultipleCheck
        ? (numericCellValue & numericValue) === numericValue
        : numericCellValue >= numericValue;
  }
};
// #endregion

export default CellIcon;
